import { Box } from '@mui/material';

import {
  NotificationsAlertIcon,
  NotificationsSolvedIcon,
  NotificationsReminderIcon,
  NotificationsIcon,
  NotificationsNewsIcon,
  NotificationsUpdatesIcon,
} from '~/shared/components/Icons/Icons';
import {
  NotificationIconType,
  NotificationTypes,
  NotificationUrgency,
} from './types';
import { notificationStyles } from '~/shared/theme/theme';
import { URGENCY_CONFIG } from './utils/severietyMapping';

interface NotificationIconProps {
  iconType: NotificationIconType;
  urgency?: NotificationUrgency;
  isResolved?: boolean;
}

const DEFAULT_TYPE = NotificationTypes.NOTIFICATION;

const ICON_BY_TYPE = {
  [NotificationTypes.ALERT]: NotificationsAlertIcon,
  [NotificationTypes.REMINDER]: NotificationsReminderIcon,
  [NotificationTypes.SOLVED]: NotificationsSolvedIcon,
  [NotificationTypes.NEWS]: NotificationsNewsIcon,
  [NotificationTypes.UPDATES]: NotificationsUpdatesIcon,
  [NotificationTypes.NOTIFICATION]: NotificationsIcon,
} satisfies Record<NotificationIconType, typeof NotificationsIcon>;

function getTypeStyles(
  iconType: NotificationIconType,
  urgency?: NotificationUrgency,
) {
  const styles = notificationStyles.types;

  // Alert icon color reflects severity
  if (iconType === NotificationTypes.ALERT && urgency) {
    const { iconBgColor, iconColor } = URGENCY_CONFIG[urgency];
    return { backgroundColor: iconBgColor, color: iconColor };
  }
  const defaultKey = DEFAULT_TYPE as keyof typeof styles;

  if (iconType in styles) {
    return styles[iconType as keyof typeof styles];
  }

  return styles[defaultKey];
}

function getIconByType(iconType: NotificationIconType) {
  const key = iconType as keyof typeof ICON_BY_TYPE;

  if (key in ICON_BY_TYPE) {
    const Icon = ICON_BY_TYPE[key];
    return <Icon fontSize="small" />;
  }

  return <NotificationsIcon fontSize="small" />;
}

export function NotificationsCardIcon({
  iconType,
  urgency,
  isResolved = false,
}: NotificationIconProps) {
  const resolvedStyles = notificationStyles.types.resolved;
  const typeStyles = isResolved
    ? resolvedStyles
    : getTypeStyles(iconType, urgency);
  const icon = getIconByType(iconType);

  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: typeStyles.backgroundColor,
        color: typeStyles.color,
        opacity: isResolved ? 0.8 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
  );
}
